/**
 * Grafici della console, in SVG scritto a mano.
 *
 * Nessuna libreria: le due forme che servono — un andamento nel tempo e un
 * confronto di grandezze — sono poche decine di righe, mentre una libreria di
 * grafici porterebbe centinaia di kilobyte, un proprio linguaggio di temi da
 * riconciliare con il nostro, e un secondo posto in cui definire i colori.
 *
 * Entrambe le forme portano **una sola serie**, e questo decide quasi tutto:
 * un solo colore (quindi nessuna legenda da leggere — il titolo dice già cosa
 * si sta guardando) e nessun problema di distinguibilità fra tinte. Il colore
 * viene dai token del tema, quindi chiaro e scuro sono già risolti.
 *
 * Le regole che seguono non sono estetiche:
 * - **un solo asse dei valori**. Due scale sovrapposte sullo stesso grafico
 *   fanno leggere una correlazione che dipende da come sono state scalate;
 * - **linea 2px, riempimento al 10%**: il dato è l'unica cosa che deve pesare;
 * - **niente valore su ogni punto**: si etichetta il massimo, il resto lo dice
 *   il passaggio del mouse;
 * - **griglia sottile e in secondo piano**, mai tratteggiata.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { localeCorrente, t } from '../i18n/lingua'
import { formatDay } from './common'

export interface Point {
  at: string
  bytes: number
  packets?: number
  flows?: number
  bps?: number
}

/** Byte in unità leggibili, base 1024 come i sistemi operativi. */
export function formatBytes(value: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let n = value
  let unit = 0
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024
    unit += 1
  }
  return `${n < 10 && unit > 0 ? n.toFixed(1) : Math.round(n)} ${units[unit]}`
}

/** Bit al secondo in unità leggibili (base 1000: è la convenzione delle reti). */
export function formatRate(bps: number): string {
  const units = ['bps', 'kbps', 'Mbps', 'Gbps', 'Tbps']
  let n = bps
  let unit = 0
  while (n >= 1000 && unit < units.length - 1) {
    n /= 1000
    unit += 1
  }
  return `${n < 10 && unit > 0 ? n.toFixed(1) : Math.round(n)} ${units[unit]}`
}

function formatClock(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString(localeCorrente(), { hour: '2-digit', minute: '2-digit' })
}

function formatStamp(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(localeCorrente(), { dateStyle: 'short', timeStyle: 'short' })
}

/** Arrotonda il massimo a un numero pulito, per avere tacche leggibili. */
function niceMax(value: number): number {
  if (value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return step * magnitude
}

/**
 * Andamento del traffico nel tempo: area a serie singola.
 *
 * L'area, e non le colonne, perché i bucket sono contigui e la continuità è
 * l'informazione: un buco nella serie deve *vedersi* come un buco, non come una
 * colonna mancante fra altre uguali.
 */
export function TrafficArea({
  points,
  height = 180,
  stepSeconds = 300,
}: {
  points: Point[]
  height?: number
  stepSeconds?: number
}) {
  const gradientId = useId()
  const [hover, setHover] = useState<number | null>(null)

  if (!points.length) {
    return (
      <p className="text-muted" style={{ margin: '24px 0', textAlign: 'center' }}>
        {t('Nessun traffico osservato in questa finestra.')}
      </p>
    )
  }

  const width = 800
  const padding = { top: 12, right: 16, bottom: 26, left: 64 }
  const plotW = width - padding.left - padding.right
  const plotH = height - padding.top - padding.bottom

  const max = niceMax(Math.max(...points.map((p) => p.bytes)))
  const x = (i: number) =>
    padding.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW)
  const y = (v: number) => padding.top + plotH - (v / max) * plotH

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.bytes)}`).join(' ')
  const area = `${line} L ${x(points.length - 1)} ${padding.top + plotH} L ${x(0)} ${
    padding.top + plotH
  } Z`

  // Si etichetta il picco e basta. Un valore su ogni punto non si legge, e la
  // tacca dell'asse porta già la scala.
  const peak = points.reduce((best, p, i) => (p.bytes > points[best].bytes ? i : best), 0)
  const ticks = [0, 0.5, 1].map((f) => f * max)

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="chart__svg"
        role="img"
        aria-label={t('Traffico osservato: {n} intervalli, massimo {max}', {
          n: points.length,
          max: formatBytes(max),
        })}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {ticks.map((value) => (
          <g key={value}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={y(value)}
              y2={y(value)}
              className="chart__grid"
            />
            <text x={padding.left - 10} y={y(value) + 4} className="chart__tick" textAnchor="end">
              {formatBytes(value)}
            </text>
          </g>
        ))}

        <path d={area} fill={`url(#${gradientId})`} />
        <path d={line} className="chart__line" />

        {/* Etichetta del solo picco. */}
        <circle cx={x(peak)} cy={y(points[peak].bytes)} r="4" className="chart__peak" />
        <text
          x={Math.min(x(peak) + 8, width - padding.right - 44)}
          y={Math.max(y(points[peak].bytes) - 8, padding.top + 10)}
          className="chart__label"
        >
          {formatBytes(points[peak].bytes)}
        </text>

        {/* Estremi dell'asse dei tempi: due riferimenti bastano, il resto lo
            dice il passaggio del mouse. */}
        <text x={padding.left} y={height - 6} className="chart__tick">
          {formatClock(points[0].at)}
        </text>
        <text x={width - padding.right} y={height - 6} className="chart__tick" textAnchor="end">
          {formatClock(points[points.length - 1].at)}
        </text>

        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={padding.top}
            y2={padding.top + plotH}
            className="chart__crosshair"
          />
        )}

        {/* Zone di aggancio larghe: il punto è piccolo, la zona sensibile no. */}
        {points.map((p, i) => (
          <rect
            key={p.at}
            x={x(i) - plotW / Math.max(points.length, 1) / 2}
            y={padding.top}
            width={Math.max(plotW / Math.max(points.length, 1), 6)}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>

      {hover !== null && (
        <div className="chart__tooltip">
          <strong>{formatStamp(points[hover].at)}</strong>
          <span>{formatBytes(points[hover].bytes)}</span>
          {points[hover].bps !== undefined && (
            <span className="text-muted">
              {t('{rate} medi su {min} min', {
                rate: formatRate(points[hover].bps!),
                min: Math.round(stepSeconds / 60),
              })}
            </span>
          )}
          {points[hover].flows !== undefined && (
            <span className="text-muted">
              {t('{n} flussi', { n: points[hover].flows!.toLocaleString(localeCorrente()) })}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export interface BarDatum {
  key: string | number
  label: string
  bytes: number
}

/**
 * Confronto di grandezze: barre orizzontali, una tinta sola.
 *
 * Orizzontali perché le etichette sono nomi di servizi (`postgresql (5432)`) e
 * in verticale finirebbero ruotate o troncate. Ordinate per valore: leggere una
 * classifica in ordine alfabetico costringe a cercare il massimo con gli occhi.
 */
export function TrafficBars({ data, max: forcedMax }: { data: BarDatum[]; max?: number }) {
  if (!data.length) {
    return <p className="text-muted">{t('Nessun dato in questa finestra.')}</p>
  }
  const max = forcedMax ?? Math.max(...data.map((d) => d.bytes), 1)

  return (
    <div className="bars">
      {data.map((d) => (
        <div className="bars__row" key={d.key}>
          <span className="bars__label" title={d.label}>
            {d.label}
          </span>
          <span className="bars__track">
            <span
              className="bars__fill"
              style={{ width: `${Math.max((d.bytes / max) * 100, 0.8)}%` }}
            />
          </span>
          <span className="bars__value">{formatBytes(d.bytes)}</span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------- categorie

export interface Category {
  key: string
  label: string
  value: number
}

/** Quante righe prima di raggruppare il resto. */
export const MAX_CATEGORY_ROWS = 8

/**
 * Confronto di grandezze fra categorie: barre orizzontali, una tinta sola.
 *
 * ## Perché orizzontali e non a torta
 *
 * La domanda è «quanto, e chi è il primo»: si legge confrontando lunghezze, che
 * l'occhio fa bene, mentre confrontare angoli lo fa male. E le etichette dei
 * tipi di CI sono parole — «macchina virtuale», «apparato di rete» — che in
 * verticale finirebbero ruotate o troncate.
 *
 * ## Una tinta sola
 *
 * È **una** misura letta su categorie diverse: il colore non porta nessuna
 * informazione in più, quindi darne uno per categoria significherebbe far
 * cercare a chi guarda un significato che non c'è. L'identità la porta
 * l'etichetta, che è sempre scritta.
 *
 * ## Ordinate, e troncate con onestà
 *
 * Dal più grande, perché la prima riga è la risposta. Oltre
 * `MAX_CATEGORY_ROWS` il resto si somma in una riga «altri N», **dichiarata**:
 * un elenco tagliato in silenzio si legge come completo, e chi guarda conclude
 * che quei tipi non esistano.
 */
export function CategoryBars({
  data,
  format = (n: number) => n.toLocaleString(localeCorrente()),
  emptyHint = t('Nessun dato da mostrare.'),
}: {
  data: Category[]
  format?: (value: number) => string
  emptyHint?: string
}) {
  const rows = categoryRows(data)
  if (!rows.length) return <p className="text-muted">{emptyHint}</p>

  const max = Math.max(...rows.map((r) => r.value), 1)
  const total = rows.reduce((sum, r) => sum + r.value, 0)

  return (
    <div className="bars" role="table">
      {rows.map((row) => {
        const share = total ? Math.round((row.value / total) * 100) : 0
        return (
          <div className="bars__row" key={row.key} role="row">
            <span className="bars__label" title={row.label} role="cell">
              {row.label}
            </span>
            <span
              className="bars__track"
              role="cell"
              // Il valore esatto al passaggio del mouse: sulla barra, che è il
              // bersaglio grande, non sul numero accanto.
              title={`${row.label}: ${format(row.value)} (${share}%)`}
            >
              <span
                className="bars__fill"
                // Mai a zero: una categoria con un valore piccolo ma vero deve
                // vedersi, altrimenti «poco» e «niente» si leggono uguali.
                style={{ width: `${Math.max((row.value / max) * 100, 1.5)}%` }}
              />
            </span>
            <span className="bars__value" role="cell">
              {format(row.value)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Le righe da disegnare: ordinate, troncate, con il resto sommato.
 *
 * Sta fuori dal componente perché è la parte che si sbaglia — e l'unica
 * verificabile senza montare un browser.
 */
export function categoryRows(data: Category[]): Category[] {
  const useful = data.filter((d) => d.value > 0)
  if (!useful.length) return []

  const sorted = [...useful].sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
  if (sorted.length <= MAX_CATEGORY_ROWS) return sorted

  const head = sorted.slice(0, MAX_CATEGORY_ROWS - 1)
  const tail = sorted.slice(MAX_CATEGORY_ROWS - 1)
  const rest = tail.reduce((sum, d) => sum + d.value, 0)
  return [
    ...head,
    { key: '__altri__', label: t('altri {n}', { n: tail.length }), value: rest },
  ]
}

// ------------------------------------------------------------------- stati

export interface StatusSlice {
  key: string
  label: string
  value: number
  /** `ok` | `warn` | `error` | `info`: i colori **riservati** agli stati. */
  tone: 'ok' | 'warn' | 'error' | 'info'
}

/**
 * Composizione per stato: una barra sola, divisa, con la legenda scritta.
 *
 * ## Il colore non basta, e non è un'opinione
 *
 * I quattro colori di stato del tema, misurati: rosso e ambra distano ΔE 3.8 in
 * deuteranopia, e ambra e verde 14.8 **a vista normale**. Chi guarda questa
 * barra affidandosi al solo colore leggerebbe male, e non se ne accorgerebbe.
 *
 * Perciò ogni segmento porta il proprio nome e il proprio numero nella legenda,
 * sempre — non come ripiego per chi non distingue i colori, ma perché «tre
 * sonde degradate» è l'informazione, e il colore serve solo a trovarla in
 * fretta. Fra un segmento e l'altro corrono 2px di superficie, così il confine
 * si vede anche quando i colori non aiutano.
 */
export function StatusStrip({
  data,
  emptyHint = t('Niente da mostrare.'),
}: {
  data: StatusSlice[]
  emptyHint?: string
}) {
  const slices = data.filter((s) => s.value > 0)
  const total = slices.reduce((sum, s) => sum + s.value, 0)
  if (!total) return <p className="text-muted">{emptyHint}</p>

  return (
    <div className="statusstrip">
      <div className="statusstrip__bar">
        {slices.map((slice) => (
          <span
            key={slice.key}
            className={`statusstrip__part statusstrip__part--${slice.tone}`}
            style={{ flexGrow: slice.value }}
            title={`${slice.label}: ${slice.value.toLocaleString(localeCorrente())}`}
          />
        ))}
      </div>
      <ul className="statusstrip__legend">
        {slices.map((slice) => (
          <li key={slice.key}>
            <span className={`statusstrip__dot statusstrip__dot--${slice.tone}`} />
            <strong>{slice.value.toLocaleString(localeCorrente())}</strong> {slice.label}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Una fetta della ruota: etichetta, valore e tono. */
export interface DonutSlice {
  key: string
  label: string
  value: number
  color: string
}

/**
 * Grafico a ruota: la proporzione a colpo d'occhio, il totale al centro.
 *
 * ## Perché una ruota e non una barra
 *
 * La domanda dei widget di riepilogo è «quanta parte del tutto» — quanti
 * apparati hanno un backup recente, quanti asset sono coperti — e una parte sul
 * tutto si legge meglio come settore di un cerchio che come segmento di una
 * striscia. La barra resta giusta per confrontare categorie fra loro.
 *
 * ## La legenda è parte del grafico
 *
 * Ogni fetta ha accanto etichetta e numero: il colore da solo non basta a chi
 * non lo distingue, e un grafico che si legge solo passandoci sopra col mouse
 * non si legge su un telefono.
 */
export function Donut({
  slices,
  centreLabel,
  onSliceClick,
  selected,
}: {
  slices: DonutSlice[]
  centreLabel?: string
  /** Se presente, fette e voci di legenda sono cliccabili: il widget apre il
   *  dettaglio di quella fetta. `selected` evidenzia la fetta aperta. */
  onSliceClick?: (key: string) => void
  selected?: string | null
}) {
  const total = slices.reduce((sum, s) => sum + s.value, 0)
  const R = 42
  const C = 2 * Math.PI * R
  let offset = 0
  const cliccabile = Boolean(onSliceClick)
  return (
    <div className="donut">
      <svg viewBox="0 0 120 120" width="132" height="132" role="img" aria-hidden="true">
        <circle cx="60" cy="60" r={R} fill="none" stroke="var(--muted-bg)" strokeWidth="16" />
        {total > 0 &&
          slices
            .filter((s) => s.value > 0)
            .map((s) => {
              const frac = s.value / total
              const el = (
                <circle
                  key={s.key}
                  cx="60"
                  cy="60"
                  r={R}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={selected === s.key ? 20 : 16}
                  strokeDasharray={`${frac * C} ${C}`}
                  strokeDashoffset={-offset * C}
                  transform="rotate(-90 60 60)"
                  style={cliccabile ? { cursor: 'pointer' } : undefined}
                  onClick={cliccabile ? () => onSliceClick!(s.key) : undefined}
                />
              )
              offset += frac
              return el
            })}
        <text
          x="60"
          y="57"
          textAnchor="middle"
          fontSize="22"
          fontWeight="700"
          fill="var(--text)"
        >
          {total.toLocaleString(localeCorrente())}
        </text>
        {centreLabel && (
          <text x="60" y="74" textAnchor="middle" fontSize="10" fill="var(--text-muted)">
            {centreLabel}
          </text>
        )}
      </svg>
      <ul className="donut__legend">
        {slices.map((s) => (
          <li
            key={s.key}
            style={cliccabile ? { cursor: 'pointer', fontWeight: selected === s.key ? 700 : undefined } : undefined}
            onClick={cliccabile ? () => onSliceClick!(s.key) : undefined}
            role={cliccabile ? 'button' : undefined}
          >
            <span className="donut__dot" style={{ background: s.color }} aria-hidden="true" />
            {s.label} <strong>{s.value.toLocaleString(localeCorrente())}</strong>
          </li>
        ))}
      </ul>
    </div>
  )
}

export interface CountPoint {
  date: string
  value: number
}

/**
 * Una serie di **conteggi** per giorno: barre verticali, una tinta sola.
 *
 * `TrafficArea` parla in byte (assi e tooltip), quindi non serve per «quanti hit
 * al giorno» o «quanti avvistamenti». Questa è la sua sorella per i conteggi:
 * un punto al giorno, zeri compresi come li manda il backend. Il colore è un
 * token del tema (`tone`), così chiaro e scuro sono già risolti e non c'è un
 * secondo posto dove definirlo.
 *
 * Disegnata in pixel veri (la larghezza la misura un `ResizeObserver`), non
 * in un riquadro stirato: le barre restano barre e le scritte restano
 * leggibili. Griglia e tick a sinistra su una scala «pulita» (1, 2, 5 × 10ⁿ),
 * le date sotto ogni tanto, il solo **picco** etichettato — un numero su ogni
 * barra non si legge —, e il passaggio del mouse (o le frecce) che dice
 * giorno e valore esatto nel riquadro. Le barre a zero non si disegnano ma
 * si interrogano: «quel giorno niente» è una risposta.
 */
export function CountSeries({
  points,
  tone = 'accent',
  emptyHint = t('Nessun dato nel periodo.'),
  label = '',
  altezza = 180,
}: {
  points: CountPoint[]
  tone?: 'accent' | 'ok' | 'warn' | 'error'
  emptyHint?: string
  label?: string
  altezza?: number
}) {
  const contenitore = useRef<HTMLDivElement>(null)
  const [larghezza, setLarghezza] = useState(720)
  const [hover, setHover] = useState<number | null>(null)
  useEffect(() => {
    const el = contenitore.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const oss = new ResizeObserver((voci) => {
      const w = voci[0]?.contentRect.width
      if (w && w > 0) setLarghezza(Math.floor(w))
    })
    oss.observe(el)
    return () => oss.disconnect()
  }, [])

  if (!points.length || points.every((p) => p.value === 0)) {
    return <p className="text-muted" style={{ margin: 0 }}>{emptyHint}</p>
  }

  const W = larghezza
  const H = altezza
  const M = { top: 20, right: 8, bottom: 24, left: 40 }
  const plotW = Math.max(40, W - M.left - M.right)
  const plotH = Math.max(40, H - M.top - M.bottom)
  const n = points.length
  const max = Math.max(1, ...points.map((p) => p.value))
  const { tetto, passoY } = scalaPulita(max, points.every((p) => Number.isInteger(p.value)))
  const y = (v: number) => M.top + plotH - (v / tetto) * plotH
  const passo = plotW / n
  const larg = Math.min(28, Math.max(2, passo - 2))
  const xDi = (i: number) => M.left + i * passo + (passo - larg) / 2
  const raggio = Math.min(3, larg / 2)
  const picco = points.findIndex((p) => p.value === max)
  // Le date sotto: circa sei, sempre la prima e l'ultima.
  const ogni = Math.max(1, Math.ceil(n / 6))
  const tickX = points.map((_, i) => i).filter((i) => i % ogni === 0 || i === n - 1)
  const tickY: number[] = []
  for (let v = 0; v <= tetto + 1e-9; v += passoY) tickY.push(v)
  const giorno = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(localeCorrente(), { day: 'numeric', month: 'short' })
  }
  const fmt = (v: number) => v.toLocaleString(localeCorrente())
  const indiceDa = (clientX: number, el: SVGSVGElement) => {
    const r = el.getBoundingClientRect()
    const x = ((clientX - r.left) / r.width) * W - M.left
    return Math.min(n - 1, Math.max(0, Math.floor(x / passo)))
  }
  const tooltipX = hover == null ? 0 : Math.min(Math.max(xDi(hover) + larg + 10, 0), W - 200)
  const tooltipY = hover == null ? 0 : Math.max(0, y(points[hover].value) - 14)

  return (
    <div className="gconteggi" ref={contenitore}>
      <svg className="gs__svg" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img"
           aria-label={label || t('Serie per giorno')} tabIndex={0}
           onMouseMove={(e) => setHover(indiceDa(e.clientX, e.currentTarget))}
           onMouseLeave={() => setHover(null)}
           onKeyDown={(e) => {
             if (e.key === 'ArrowLeft') { setHover((h) => Math.max(0, (h ?? n) - 1)); e.preventDefault() }
             if (e.key === 'ArrowRight') { setHover((h) => Math.min(n - 1, (h ?? -1) + 1)); e.preventDefault() }
             if (e.key === 'Escape') setHover(null)
           }}>
        {tickY.map((v) => (
          <g key={v}>
            <line className="gs__grid" x1={M.left} x2={M.left + plotW} y1={y(v)} y2={y(v)} />
            <text className="gs__tick" x={M.left - 6} y={y(v) + 4} textAnchor="end">{fmt(v)}</text>
          </g>
        ))}
        {tickX.map((i) => (
          <text key={i} className="gs__tick" x={xDi(i) + larg / 2} y={H - 6}
                textAnchor={i === n - 1 ? 'end' : i === 0 ? 'start' : 'middle'}>{giorno(points[i].date)}</text>
        ))}
        {points.map((p, i) => {
          if (p.value <= 0) return null
          const top = y(p.value)
          const h = M.top + plotH - top
          const x = xDi(i)
          const r = Math.min(raggio, h)
          const d = `M${x},${M.top + plotH} v${-(h - r)} a${r},${r} 0 0 1 ${r},${-r} h${larg - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - r} z`
          return <path key={p.date} d={d} className={`gconteggi__barra gconteggi__barra--${tone}${hover === i ? ' gconteggi__barra--hover' : ''}`} />
        })}
        {picco >= 0 && (
          <text className="gs__label" x={xDi(picco) + larg / 2} y={y(max) - 6} textAnchor="middle">{fmt(max)}</text>
        )}
        {hover != null && (
          <line className="gs__cross" x1={xDi(hover) + larg / 2} x2={xDi(hover) + larg / 2} y1={M.top} y2={M.top + plotH} />
        )}
      </svg>
      {hover != null && (
        <div className="gs__tooltip" style={{ left: tooltipX, top: tooltipY }} role="status">
          <strong>{formatDay(points[hover].date)}</strong>
          <span>{fmt(points[hover].value)}</span>
        </div>
      )}
    </div>
  )
}

/** Il tetto dell'asse e il passo dei tick: 1, 2 o 5 per potenza di dieci, con
 * quattro o cinque righe al massimo. Un asse che finisce a 37 non si legge. */
export function scalaPulita(max: number, intero = false): { tetto: number; passoY: number } {
  const grezzo = Math.max(1, max) / 4
  const pot = 10 ** Math.floor(Math.log10(grezzo))
  const base = grezzo / pot
  // Per i conteggi un passo sotto l'unità darebbe tacche «0,5 dispositivi».
  const passoY = Math.max(intero ? 1 : 0, (base <= 1 ? 1 : base <= 2 ? 2 : base <= 5 ? 5 : 10) * pot)
  return { tetto: Math.ceil(Math.max(1, max) / passoY) * passoY, passoY }
}

// ------------------------------------------------------- serie impilate

/** Una serie del grafico impilato: chiave, etichetta, tinta. */
export interface StackSerie {
  key: string
  label: string
  /** Una delle tinte `--serie-1…4`, o il grigio di «altri». */
  color: string
}

export interface StackPoint {
  date: string
  values: Record<string, number>
}

/** Le tinte delle serie in ordine fisso, e il grigio per «altri». */
export const TINTE_SERIE = ['var(--serie-1)', 'var(--serie-2)', 'var(--serie-3)', 'var(--serie-4)']
export const TINTA_ALTRI = 'var(--text-muted)'

/**
 * Conteggi per giorno divisi per serie: barre impilate, una tinta per serie.
 *
 * La sorella di `CountSeries` quando il totale si legge **con la sua
 * composizione** (quanti usi al giorno, e di quale servizio). Le regole:
 * - **al più quattro serie** con nome, nelle tinte `--serie-1…4` in ordine
 *   fisso (verificate anche per chi non distingue i colori); il resto va in
 *   «altri», grigio, sempre in cima alla pila;
 * - la **legenda è sempre scritta**, con il totale del periodo per serie: il
 *   colore da solo non basta;
 * - fra un segmento e l'altro 2px di superficie, così il confine si vede anche
 *   quando le tinte non aiutano; arrotondata solo la cima della pila;
 * - il passaggio del mouse (o le frecce) dice giorno, ogni serie e il totale.
 */
export function StackedCountSeries({
  points,
  series,
  altezza = 200,
  label = '',
  emptyHint = t('Nessun dato nel periodo.'),
}: {
  points: StackPoint[]
  series: StackSerie[]
  altezza?: number
  label?: string
  emptyHint?: string
}) {
  const contenitore = useRef<HTMLDivElement>(null)
  const [larghezza, setLarghezza] = useState(720)
  const [hover, setHover] = useState<number | null>(null)
  useEffect(() => {
    const el = contenitore.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const oss = new ResizeObserver((voci) => {
      const w = voci[0]?.contentRect.width
      if (w && w > 0) setLarghezza(Math.floor(w))
    })
    oss.observe(el)
    return () => oss.disconnect()
  }, [])

  const totaleDi = (p: StackPoint) => series.reduce((s, x) => s + (p.values[x.key] ?? 0), 0)
  const totali = points.map(totaleDi)
  if (!points.length || totali.every((v) => v === 0)) {
    return <p className="text-muted" style={{ margin: 0 }}>{emptyHint}</p>
  }

  const W = larghezza
  const H = altezza
  const M = { top: 20, right: 8, bottom: 24, left: 40 }
  const plotW = Math.max(40, W - M.left - M.right)
  const plotH = Math.max(40, H - M.top - M.bottom)
  const n = points.length
  const max = Math.max(1, ...totali)
  const { tetto, passoY } = scalaPulita(max, totali.every((v) => Number.isInteger(v)))
  const y = (v: number) => M.top + plotH - (v / tetto) * plotH
  const passo = plotW / n
  const larg = Math.min(28, Math.max(2, passo - 2))
  const xDi = (i: number) => M.left + i * passo + (passo - larg) / 2
  const picco = totali.indexOf(max)
  const ogni = Math.max(1, Math.ceil(n / 6))
  const tickX = points.map((_, i) => i).filter((i) => i % ogni === 0 || i === n - 1)
  const tickY: number[] = []
  for (let v = 0; v <= tetto + 1e-9; v += passoY) tickY.push(v)
  const giorno = (iso: string) => {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(localeCorrente(), { day: 'numeric', month: 'short' })
  }
  const fmt = (v: number) => v.toLocaleString(localeCorrente())
  const indiceDa = (clientX: number, el: SVGSVGElement) => {
    const r = el.getBoundingClientRect()
    const x = ((clientX - r.left) / r.width) * W - M.left
    return Math.min(n - 1, Math.max(0, Math.floor(x / passo)))
  }
  const sommaSerie = (k: string) => points.reduce((s, p) => s + (p.values[k] ?? 0), 0)
  const tooltipX = hover == null ? 0 : Math.min(Math.max(xDi(hover) + larg + 10, 0), W - 220)
  const tooltipY = hover == null ? 0 : Math.max(0, y(totali[hover]) - 14)

  return (
    <div className="gconteggi" ref={contenitore}>
      <svg className="gs__svg" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img"
           aria-label={label || t('Serie per giorno')} tabIndex={0}
           onMouseMove={(e) => setHover(indiceDa(e.clientX, e.currentTarget))}
           onMouseLeave={() => setHover(null)}
           onKeyDown={(e) => {
             if (e.key === 'ArrowLeft') { setHover((h) => Math.max(0, (h ?? n) - 1)); e.preventDefault() }
             if (e.key === 'ArrowRight') { setHover((h) => Math.min(n - 1, (h ?? -1) + 1)); e.preventDefault() }
             if (e.key === 'Escape') setHover(null)
           }}>
        {tickY.map((v) => (
          <g key={v}>
            <line className="gs__grid" x1={M.left} x2={M.left + plotW} y1={y(v)} y2={y(v)} />
            <text className="gs__tick" x={M.left - 6} y={y(v) + 4} textAnchor="end">{fmt(v)}</text>
          </g>
        ))}
        {tickX.map((i) => (
          <text key={i} className="gs__tick" x={xDi(i) + larg / 2} y={H - 6}
                textAnchor={i === n - 1 ? 'end' : i === 0 ? 'start' : 'middle'}>{giorno(points[i].date)}</text>
        ))}
        {points.map((p, i) => {
          let base = 0
          const presenti = series.filter((s) => (p.values[s.key] ?? 0) > 0)
          return (
            <g key={p.date} opacity={hover == null || hover === i ? 1 : 0.55}>
              {presenti.map((s, j) => {
                const v = p.values[s.key] ?? 0
                const sotto = y(base)
                const sopra = y(base + v)
                base += v
                const cima = j === presenti.length - 1
                // 2px di superficie fra un segmento e il successivo.
                const h = Math.max(1, sotto - sopra - (cima ? 0 : 2))
                const top = sotto - h
                const x = xDi(i)
                const r = cima ? Math.min(4, larg / 2, h) : 0
                const d = `M${x},${top + h} v${-(h - r)} a${r},${r} 0 0 1 ${r},${-r} h${larg - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${h - r} z`
                return <path key={s.key} d={d} fill={s.color} />
              })}
            </g>
          )
        })}
        {picco >= 0 && (
          <text className="gs__label" x={xDi(picco) + larg / 2} y={y(max) - 6} textAnchor="middle">{fmt(max)}</text>
        )}
        {hover != null && (
          <line className="gs__cross" x1={xDi(hover) + larg / 2} x2={xDi(hover) + larg / 2} y1={M.top} y2={M.top + plotH} />
        )}
      </svg>
      {hover != null && (
        <div className="gs__tooltip" style={{ left: tooltipX, top: tooltipY }} role="status">
          <strong>{formatDay(points[hover].date)}</strong>
          {series.filter((s) => (points[hover].values[s.key] ?? 0) > 0).map((s) => (
            <span key={s.key} className="gimpila__riga">
              <span className="donut__dot" style={{ background: s.color }} aria-hidden="true" />
              {s.label}<strong>{fmt(points[hover].values[s.key] ?? 0)}</strong>
            </span>
          ))}
          <span className="gimpila__riga gimpila__totale">{t('Totale')}<strong>{fmt(totali[hover])}</strong></span>
        </div>
      )}
      {series.length > 1 && (
        <ul className="gimpila__legenda">
          {series.map((s) => (
            <li key={s.key}>
              <span className="donut__dot" style={{ background: s.color }} aria-hidden="true" />
              {s.label} <strong>{fmt(sommaSerie(s.key))}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ----------------------------------------------------- mappa giorno × ora

/**
 * Quando succede una cosa: sette righe (lunedì → domenica) per ventiquattro
 * ore, una tinta sola dal chiaro allo scuro.
 *
 * È una **grandezza** (quanti), quindi una tinta sola che si scurisce, mai un
 * arcobaleno; la cella vuota resta del fondo, così «niente» non si confonde
 * con «poco». La scala ha cinque gradini sui quantili dei valori non nulli —
 * con pochi picchi enormi una scala lineare lascerebbe tutto il resto chiaro —
 * ed è scritta sotto. A destra il totale di ogni giorno; il passaggio del mouse
 * dice giorno, ora e valore.
 */
export function HeatmapSettimana({
  celle,
  giorni,
  emptyHint = t('Nessun dato nel periodo.'),
  unita = '',
}: {
  /** `celle[giorno][ora]`, giorno 0 = lunedì. */
  celle: number[][]
  /** Le sette etichette dei giorni, dal lunedì. */
  giorni: string[]
  emptyHint?: string
  unita?: string
}) {
  const contenitore = useRef<HTMLDivElement>(null)
  const [larghezza, setLarghezza] = useState(720)
  const [hover, setHover] = useState<[number, number] | null>(null)
  useEffect(() => {
    const el = contenitore.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const oss = new ResizeObserver((voci) => {
      const w = voci[0]?.contentRect.width
      if (w && w > 0) setLarghezza(Math.floor(w))
    })
    oss.observe(el)
    return () => oss.disconnect()
  }, [])

  const valori = celle.flat().filter((v) => v > 0).sort((a, b) => a - b)
  if (!valori.length) return <p className="text-muted" style={{ margin: 0 }}>{emptyHint}</p>

  // Cinque gradini sui quantili dei valori non nulli.
  const soglie = [0.2, 0.4, 0.6, 0.8].map((q) => valori[Math.min(valori.length - 1, Math.floor(q * valori.length))])
  const gradino = (v: number) => (v <= 0 ? -1 : soglie.filter((s) => v > s).length)
  const PERC = [22, 40, 58, 78, 100]
  const tinta = (g: number) => (g < 0 ? 'var(--muted-bg)' : `color-mix(in srgb, var(--accent) ${PERC[g]}%, var(--surface))`)

  const W = larghezza
  const M = { top: 6, right: 56, bottom: 22, left: 44 }
  const cellaW = Math.max(8, (W - M.left - M.right) / 24)
  const cellaH = Math.min(26, Math.max(14, cellaW * 0.8))
  const H = M.top + 7 * cellaH + M.bottom
  const fmt = (v: number) => v.toLocaleString(localeCorrente())
  const totaliGiorno = celle.map((r) => r.reduce((s, v) => s + v, 0))

  return (
    <div className="gconteggi" ref={contenitore}>
      <svg className="gs__svg" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img"
           aria-label={t('Uso per giorno della settimana e ora')} onMouseLeave={() => setHover(null)}>
        {giorni.map((g, d) => (
          <text key={g} className="gs__tick" x={M.left - 6} y={M.top + d * cellaH + cellaH / 2 + 4} textAnchor="end">{g}</text>
        ))}
        {celle.map((riga, d) => riga.map((v, h) => (
          <rect key={`${d}-${h}`} x={M.left + h * cellaW + 1} y={M.top + d * cellaH + 1}
                width={Math.max(1, cellaW - 2)} height={Math.max(1, cellaH - 2)} rx={3}
                fill={tinta(gradino(v))}
                stroke={hover && hover[0] === d && hover[1] === h ? 'var(--text)' : 'none'}
                onMouseEnter={() => setHover([d, h])} />
        )))}
        {totaliGiorno.map((v, d) => (
          <text key={d} className="gs__tick" x={W - M.right + 8} y={M.top + d * cellaH + cellaH / 2 + 4}>{fmt(v)}</text>
        ))}
        {[0, 3, 6, 9, 12, 15, 18, 21].map((h) => (
          <text key={h} className="gs__tick" x={M.left + h * cellaW} y={H - 6}>{`${String(h).padStart(2, '0')}`}</text>
        ))}
      </svg>
      {hover && (
        <div className="gs__tooltip" role="status"
             style={{ left: Math.min(M.left + (hover[1] + 1) * cellaW + 8, W - 200), top: M.top + hover[0] * cellaH }}>
          <strong>{giorni[hover[0]]} {String(hover[1]).padStart(2, '0')}:00–{String((hover[1] + 1) % 24).padStart(2, '0')}:00</strong>
          <span>{fmt(celle[hover[0]][hover[1]])} {unita}</span>
        </div>
      )}
      <div className="gheat__scala" aria-hidden="true">
        <span>{t('meno')}</span>
        {PERC.map((_, g) => <span key={g} className="gheat__cella" style={{ background: tinta(g) }} />)}
        <span>{t('più')}</span>
      </div>
    </div>
  )
}
